import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowDownCircle, ArrowUpCircle, Check, CircleDot, ClipboardCheck, FileText, Percent, RefreshCw, ShieldCheck, TrendingDown, XCircle, type LucideIcon } from 'lucide-react';
import { StatusChip, type StatusTone } from '../abf/components/StatusChip';
import { Card, CardHead, INK } from '../abf/onboarding/review/reviewStyles';
import { Note, SmallButton, SummaryGrid } from '../abf/onboarding/review/contractReviewStyles';
import { Table, money, words } from '../abf/board/boardShared';
import type { CreditActionKind } from '../../components/credit/CreditLoanActionModals';
import { agreementReviewPath } from '../../utils/loanNavigation';
import { getPcServicing, pcErrorMessage } from './api';
import type { PcServicing, PcServicingEvent } from './types';

/**
 * The Servicing workspace of a private credit facility (PC workflow parity, slice 3): the dates the
 * record states, every lifecycle event in the lane a credit desk works it in — each with its own
 * journey (requested → applied → evidence: the tickets it emitted and the position it moved) — the
 * tickets not yet posted, and the funding control's own verdict. The actions are the facility's
 * existing servicing forms, opened from the lane they belong to; nothing here books anything.
 */

const Shell = styled.div`
  color: ${INK.body}; font-size: 14px; line-height: 1.5;
  h2, h3, p { margin: 0; } h2 { font-size: 16px; color: ${INK.strong}; } h3 { font-size: 14px; color: ${INK.strong}; }
  > * + * { margin-top: 14px; }
`;
const Upcoming = styled(SummaryGrid)`
  grid-template-columns: repeat(4, minmax(0, 1fr)); margin: 0; padding: 12px 0 14px;
  strong { font-size: 15px; } small { display: block; color: ${INK.muted}; font-size: 12px; margin-top: 3px; }
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;
const Lane = styled(Card)`
  .lane-actions { display: flex; flex-wrap: wrap; gap: 8px; }
  .events { display: grid; gap: 10px; margin-top: 12px; }
  .empty { color: ${INK.faint}; font-size: 13px; padding: 6px 0; }
`;
const ActionButton = styled.button<{ $color: string }>`
  display: inline-flex; align-items: center; gap: 7px; padding: 7px 11px; border-radius: 8px; cursor: pointer;
  font: inherit; font-size: 12.5px; font-weight: 700; color: ${INK.body}; background: transparent;
  border: 1px solid ${INK.lineStrong}; transition: border-color .15s, background .15s;
  svg { color: ${p => p.$color}; }
  &:hover { border-color: ${p => p.$color}; background: ${INK.raised}; }
  &:disabled { opacity: .45; cursor: not-allowed; }
`;
const Event = styled.div`
  border: 1px solid ${INK.line}; border-radius: 10px; background: ${INK.raised};
  .head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px; padding: 10px 12px; border-bottom: 1px solid ${INK.line}; }
  .head strong { font-size: 13.5px; color: ${INK.strong}; }
  .head .amount { font-variant-numeric: tabular-nums; font-weight: 700; }
  .steps { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .step { padding: 10px 12px; border-left: 1px solid ${INK.line}; min-width: 0; }
  .step:first-child { border-left: 0; }
  .step .k { display: flex; align-items: center; gap: 6px; font-size: 10.5px; text-transform: uppercase; letter-spacing: .07em; color: ${INK.faint}; font-weight: 800; }
  .step .k svg { color: #7cddbe; }
  .step .v { margin-top: 4px; font-size: 13px; color: ${INK.body}; overflow-wrap: anywhere; }
  .step .s { margin-top: 2px; font-size: 12px; color: ${INK.muted}; overflow-wrap: anywhere; }
  .step a { color: ${INK.accentSoft}; }
  @media (max-width: 900px) { .steps { grid-template-columns: 1fr; } .step { border-left: 0; border-top: 1px solid ${INK.line}; } .step:first-child { border-top: 0; } }
`;

type LaneKey = 'FUND_AND_REPAY' | 'ECONOMICS' | 'CREDIT';
const LANES: Array<{ key: LaneKey; title: string; description: string; actions: Array<{ kind: CreditActionKind; label: string; Icon: LucideIcon; color: string }> }> = [
  { key: 'FUND_AND_REPAY', title: 'Fund and repay', description: 'Drawdowns, DDTL draws, paydowns and the payoff. Each moves principal through a ticket that posts to the book.', actions: [
    { kind: 'drawdown', label: 'Record a drawdown', Icon: ArrowUpCircle, color: '#818cf8' },
    { kind: 'paydown', label: 'Record a paydown', Icon: ArrowDownCircle, color: '#34d399' },
    { kind: 'payoff', label: 'Record the maturity / payoff', Icon: Check, color: '#7cddbe' },
  ] },
  { key: 'ECONOMICS', title: 'Economics', description: 'Rate resets, interest and fee settlements, amendments and PIK: what the facility earns and on which terms.', actions: [
    { kind: 'rate-reset', label: 'Rate reset', Icon: RefreshCw, color: '#7dd3fc' },
    { kind: 'interest', label: 'Interest / fee settlement', Icon: Percent, color: '#6ee7b7' },
    { kind: 'amend', label: 'Amend the agreement', Icon: FileText, color: '#c4b5fd' },
    { kind: 'pc-pik-election', label: 'PIK election', Icon: Percent, color: '#fbbf24' },
    { kind: 'pc-pik-capitalize', label: 'Capitalise PIK', Icon: TrendingDown, color: '#fbbf24' },
  ] },
  { key: 'CREDIT', title: 'Credit oversight', description: 'Covenant tests, writedowns and the workout: the credit, not the cash.', actions: [
    { kind: 'covenant-test', label: 'Record a covenant test', Icon: ClipboardCheck, color: '#67e8f9' },
    { kind: 'writedown', label: 'Record a writedown', Icon: TrendingDown, color: '#f87171' },
  ] },
];

const statusTone = (status: string): StatusTone => status === 'APPLIED' ? 'done' : status === 'FAILED' ? 'critical' : 'progress';
const ticketTone = (status: string): StatusTone => ['POSTED', 'SETTLED'].includes(status) ? 'done' : ['VOIDED', 'REVERSED', 'CANCELLED', 'PRECLEAR_REJECTED'].includes(status) ? 'critical' : 'progress';
/** F-PCP-024: event labels as the desk says them, not title-cased enum names. */
const EVENT_LABELS: Record<string, string> = {
  DDTL_DRAW: 'DDTL draw', DRAWDOWN: 'Drawdown', PAYDOWN: 'Paydown', RATE_RESET: 'Rate reset', INTEREST_PAYMENT: 'Interest payment',
  COMMITMENT_FEE: 'Commitment fee', ACCRUED_INTEREST: 'Accrued interest', AMENDMENT: 'Amendment', PIK_ELECTION: 'PIK election',
  PIK_CAPITALIZATION: 'PIK capitalisation', PIK_CAPITALISATION: 'PIK capitalisation', WRITEDOWN: 'Writedown', MATURITY: 'Maturity / payoff',
  PAYOFF: 'Payoff', PREPAYMENT: 'Prepayment', REVOLVER_DRAW: 'Revolver draw', REVOLVER_REPAYMENT: 'Revolver repayment', COVENANT_TEST: 'Covenant test',
};
const typeWords = (type: string) => EVENT_LABELS[type] || words(type).replace(/^./, c => c.toUpperCase());
/** F-PCP-023: a principal ticket is stated as par at its price, then the cash it moved; a cash-only ticket as its cash. */
const ticketWords = (t: { transactionType: string; quantity: number | null; notional: number | null; netAmount: number | null; price?: number | null }, currency: string) => {
  const par = t.notional != null && Number(t.notional) ? Number(t.notional) : t.quantity != null && Number(t.quantity) ? Number(t.quantity) : null;
  const cash = t.netAmount != null && Number(t.netAmount) ? Number(t.netAmount) : null;
  if (par == null && cash == null) return words(t.transactionType);
  if (par == null) return `${words(t.transactionType)} · ${money(cash, currency)} cash`;
  const price = t.price != null && Number(t.price) && Number(t.price) !== 100 ? ` at ${Number(t.price).toFixed(2)}` : '';
  const cashPart = cash != null && Math.abs(cash - par) > 0.005 ? ` · ${money(cash, currency)} cash` : '';
  return `${words(t.transactionType)} · ${money(par, currency)} par${price}${cashPart}`;
};

const EventCard: React.FC<{ event: PcServicingEvent; currency: string }> = ({ event, currency }) => {
  const own = event.tickets.filter(t => t.relation === 'OWN');
  const shared = event.tickets.filter(t => t.relation !== 'OWN');
  return <Event data-status={event.status}>
    <div className="head">
      <div><strong>{typeWords(event.eventType)}</strong> <span style={{ color: INK.muted, fontSize: 12.5 }}>· {event.eventDate || 'no date'}{event.portfolioCode ? ` · ${event.portfolioCode}` : ''}</span></div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {event.amount != null && <span className="amount">{money(event.amount, currency)}{event.baseRate != null ? ` · base ${Number(event.baseRate).toFixed(2)}%` : ''}</span>}
        <StatusChip tone={statusTone(event.status)} label={words(event.status)} />
      </div>
    </div>
    <div className="steps">
      <div className="step">
        <div className="k"><CircleDot size={12} /> Requested</div>
        <div className="v">{event.source.kind === 'DOCUMENT'
          ? <>From <Link to={agreementReviewPath(event.source.jobId)}>{event.source.documentName || words(event.source.documentType || 'document')}</Link></>
          : <>Typed in{event.source.by ? ` by ${event.source.by}` : ''}</>}</div>
        <div className="s">{event.source.kind === 'DOCUMENT'
          ? `${words(event.source.documentType || '')}${event.source.templateVersion ? ` · read with template v${event.source.templateVersion}` : ''}`
          : 'The facility\'s own servicing form'}</div>
      </div>
      <div className="step">
        <div className="k">{event.status === 'APPLIED' ? <Check size={12} /> : event.status === 'FAILED' ? <XCircle size={12} color="#f87171" /> : <CircleDot size={12} />} Applied</div>
        <div className="v">{event.status === 'APPLIED' ? `Applied ${event.eventDate || ''}` : event.status === 'FAILED' ? 'Failed' : words(event.status)}{event.createdBy ? ` · ${event.createdBy}` : ''}</div>
        <div className="s">{event.message || '—'}</div>
      </div>
      <div className="step">
        <div className="k"><ShieldCheck size={12} /> Evidence</div>
        <div className="v">{own.length === 0 && shared.length === 0 ? <span style={{ color: INK.faint }}>No ticket emitted</span>
          : own.map(t => <div key={t.id}>Ticket #{t.id} · {ticketWords(t as any, currency)} <StatusChip tone={ticketTone(t.status)} label={words(t.status)} /></div>)}
          {shared.map(t => <div key={t.id} style={{ color: INK.muted, fontSize: 12.5 }}>Same document: ticket #{t.id} {words(t.transactionType)} <StatusChip tone={ticketTone(t.status)} label={words(t.status)} /></div>)}</div>
        <div className="s">{event.position ? `Position ${event.position.positionId}: ${money(event.position.quantity, currency)} from ${event.position.validFrom}${event.position.validTo && !event.position.validTo.startsWith('9999') ? ` to ${event.position.validTo}` : ''}${event.position.superseded ? ' (superseded)' : ' (current)'}` : 'No position change recorded'}</div>
      </div>
    </div>
  </Event>;
};

interface Props {
  loanId: number;
  refreshKey?: number | string;
  /** Opens the facility's existing servicing form. */
  onAction: (kind: CreditActionKind) => void;
}

const ServicingWorkspace: React.FC<Props> = ({ loanId, refreshKey, onAction }) => {
  const [data, setData] = useState<PcServicing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { setData(await getPcServicing(loanId)); setError(null); }
    catch (failure) { setError(pcErrorMessage(failure, 'The servicing journey could not be read.')); }
    finally { setBusy(false); }
  }, [loanId]);
  useEffect(() => { void load(); }, [load, refreshKey]);
  const dated = useMemo(() => (data?.upcoming || []).filter(u => u.kind !== 'NOT_RECORDED'), [data]);
  const notRecorded = useMemo(() => (data?.upcoming || []).find(u => u.kind === 'NOT_RECORDED')?.items || {}, [data]);
  if (!data) return <Note role={error ? 'alert' : 'status'}><p>{error || 'Reading the servicing journey…'}</p></Note>;
  const fc = data.fundingControl;
  const currency = data.currency || 'USD';

  return <Shell aria-label="Servicing">
    <Card>
      <CardHead><div><h2>What the record says is coming</h2><p>Dates the facility itself states, as recorded. A date it does not hold is not projected.</p></div>
        <SmallButton type="button" onClick={() => void load()} disabled={busy}><RefreshCw size={14} /> Refresh</SmallButton></CardHead>
      <Upcoming aria-label="Upcoming dates">
        {dated.map(u => <div key={u.kind}><span>{u.label}</span><strong>{u.date}</strong><small>{u.daysAway != null && u.daysAway >= 0 ? `in ${u.daysAway} day${u.daysAway === 1 ? '' : 's'} · ` : u.daysAway != null ? `${Math.abs(u.daysAway)} days ago · ` : ''}{u.detail}</small></div>)}
        {Object.entries(notRecorded).filter(([, v]) => v).map(([k, v]) => <div key={k}><span>{k === 'nextInterestPayment' ? 'Next interest payment' : 'Next rate reset'}</span><strong style={{ color: INK.faint }}>Not recorded</strong><small>{String(v).replace(/^not recorded: /, '')}</small></div>)}
        {dated.length === 0 && Object.values(notRecorded).every(v => !v) && <div><span>Nothing dated</span><strong style={{ color: INK.faint }}>—</strong></div>}
      </Upcoming>
      <Note style={{ marginTop: 10 }} role="status">
        <p><strong>Next drawdown · funding control: {words(fc.verdict)}.</strong> {fc.statement}</p>
      </Note>
    </Card>

    {error && <Note role="alert"><p>{error}</p></Note>}

    {data.pendingTickets.length > 0 && <Card>
      <CardHead><div><h2>Tickets not yet posted</h2><p>Each stays here until it posts to the book or is voided; the Blotter owns its lifecycle.</p></div></CardHead>
      <Table style={{ margin: 0 }}>
        <thead><tr><th>Ticket</th><th>Type</th><th>Status</th><th>Trade date</th><th className="num">Notional</th><th>Portfolio</th></tr></thead>
        <tbody>{data.pendingTickets.map(t => <tr key={t.id}>
          <td><Link to={`/bond-transactions?${new URLSearchParams({ query: String(t.id) })}`}>#{t.id}</Link></td><td>{words(t.transactionType)}</td>
          <td><StatusChip tone={ticketTone(t.status)} label={words(t.status)} /></td><td>{t.tradeDate || '—'}</td>
          <td className="num">{money(t.notional, currency)}</td><td>{t.portfolioId ?? <span style={{ color: INK.faint }}>none</span>}</td>
        </tr>)}</tbody>
      </Table>
    </Card>}

    {LANES.map(lane => {
      const entry = data.lanes[lane.key] || { events: [], applied: 0, failed: 0 };
      return <Lane key={lane.key} aria-label={lane.title}>
        <CardHead>
          <div><h2>{lane.title}</h2><p>{lane.description} {entry.events.length ? `${entry.applied} applied${entry.failed ? `, ${entry.failed} failed` : ''}.` : 'No event recorded yet.'}</p></div>
          <div className="lane-actions">
            {lane.actions.map(a => <ActionButton key={a.kind} type="button" $color={a.color} onClick={() => onAction(a.kind)}
              disabled={a.kind === 'drawdown' && fc.verdict === 'NOT_MET'}
              title={a.kind === 'drawdown' && fc.verdict === 'NOT_MET' ? fc.statement : undefined}><a.Icon size={14} /> {a.label}</ActionButton>)}
          </div>
        </CardHead>
        <div className="events">
          {entry.events.map(event => <EventCard key={event.id} event={event} currency={currency} />)}
          {entry.events.length === 0 && <div className="empty">Nothing recorded in this lane. A notice imported in Document Processing or a form above records the first event.</div>}
        </div>
      </Lane>;
    })}

    <p style={{ color: INK.faint, fontSize: 12.5 }}>{data.totals.events} event{data.totals.events === 1 ? '' : 's'}: {data.totals.fromDocuments} from documents, {data.totals.typedIn} typed in. Both lanes record the same event through the same handler.</p>
  </Shell>;
};

export default ServicingWorkspace;
